/**
 * Skeleton placeholders. Renders a low-contrast pulsing block in the shape
 * of the content that will load. Feels noticeably faster than a centered
 * spinner — the eye stops looking for "is it broken" and just waits.
 *
 *   <Skeleton className="h-4 w-32" />
 *   <SkeletonText lines={3} />
 *   <SkeletonCard />
 *
 * The animation uses a single global CSS keyframe registered once. No
 * per-element framer-motion → cheap on the GPU even with dozens visible.
 */
import React from 'react';

// One-time stylesheet injection. Hot-module-reload safe (guarded by id).
function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('dm-skeleton-keyframes')) return;
  const s = document.createElement('style');
  s.id = 'dm-skeleton-keyframes';
  s.textContent = `
    @keyframes dmSkeletonShimmer {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    .dm-skel {
      position: relative;
      overflow: hidden;
      background:
        linear-gradient(
          90deg,
          rgba(255,255,255,0.03) 0%,
          rgba(255,255,255,0.08) 50%,
          rgba(255,255,255,0.03) 100%
        );
      background-size: 200% 100%;
      animation: dmSkeletonShimmer 1.4s ease-in-out infinite;
      border-radius: 6px;
    }
  `;
  document.head.appendChild(s);
}

export default function Skeleton({ className = '', style, ...rest }) {
  ensureKeyframes();
  return <div className={`dm-skel ${className}`} style={style} aria-hidden {...rest} />;
}

/** Multiple lines of text. Last line is narrower for natural look. */
export function SkeletonText({ lines = 3, className = '' }) {
  ensureKeyframes();
  return (
    <div className={`space-y-2 ${className}`} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="dm-skel"
          style={{ height: 12, width: i === lines - 1 ? '60%' : '100%' }}
        />
      ))}
    </div>
  );
}

/** Generic card with a title bar + body. Useful as a table-row placeholder. */
export function SkeletonCard({ className = '' }) {
  ensureKeyframes();
  return (
    <div
      className={`rounded-xl p-4 space-y-3 ${className}`}
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
      aria-hidden
    >
      <div className="flex items-center gap-3">
        <Skeleton style={{ width: 36, height: 36, borderRadius: 8 }} />
        <div className="flex-1 space-y-2">
          <Skeleton style={{ height: 12, width: '40%' }} />
          <Skeleton style={{ height: 10, width: '70%' }} />
        </div>
      </div>
      <Skeleton style={{ height: 10, width: '90%' }} />
    </div>
  );
}

/** Stat-card placeholder matching the Aether-Dashboard overview cards. */
export function SkeletonStatCard({ className = '' }) {
  ensureKeyframes();
  return (
    <div
      className={`p-6 rounded-2xl ${className}`}
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
      aria-hidden
    >
      <Skeleton style={{ height: 12, width: '40%', marginBottom: 14 }} />
      <Skeleton style={{ height: 28, width: '60%' }} />
    </div>
  );
}
